import React, { useState, useEffect, useCallback } from 'react';
import { getExamPortal, getEvaluation } from '../services/api';

/**
 * Assessment Portal Hub Page (/examportal)
 * - Shows "Start Assessment" button which opens Session 1
 * - Sessions 2 & 3 are triggered by popups on the session tabs
 * - Shows evaluation results after all sessions complete
 */
function ExamPortal() {
  const [data, setData] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [examStarted, setExamStarted] = useState(false);

  const fetchPortalData = useCallback(async () => {
    try {
      const result = await getExamPortal();
      setData(result);
      setError('');

      const anyStarted = result?.sessions?.some(s => s.status !== 'not_started');
      if (anyStarted) setExamStarted(true);

      const allDone = result?.sessions?.every(s =>
        s.status === 'completed' || s.status === 'completed_incomplete'
      );
      const examExpired = result?.exam_time_remaining <= 0;

      if (allDone || examExpired) {
        try {
          const evalResult = await getEvaluation();
          setEvaluation(evalResult);
        } catch (e) {
          console.error('Failed to fetch evaluation:', e);
        }
      }
    } catch (err) {
      if (err.message?.includes('401')) {
        window.location.href = `${process.env.PUBLIC_URL || ''}/login`;
        return;
      }
      setError('Failed to load assessment portal. Please check your login.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortalData();
    const interval = setInterval(fetchPortalData, 5000);
    return () => clearInterval(interval);
  }, [fetchPortalData]);

  const handleStartExam = () => {
    window.open(`${process.env.PUBLIC_URL || ''}/examportal/session/1`, '_blank');
    setExamStarted(true);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="portal-page">
        <div className="loading">Loading assessment portal...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-page">
        <div className="error-card">
          <p>{error}</p>
          <a href={`${process.env.PUBLIC_URL || ''}/login`}>Go to Login</a>
        </div>
      </div>
    );
  }

  const allComplete = data?.sessions?.every(s =>
    s.status === 'completed' || s.status === 'completed_incomplete'
  ) || data?.exam_time_remaining <= 0;

  if (allComplete && evaluation) {
    return (
      <div className="portal-page">
        <div className="portal-card">
          <h1>🎉 Assessment Complete</h1>
          <p className="welcome">Thank you, {data.candidate_name}!</p>
          <p>Your assessment has been submitted successfully.</p>

          <div className="eval-summary">
            <div className="eval-stat">
              <span className="eval-stat-value">{evaluation.summary?.total_questions_answered || 0}</span>
              <span className="eval-stat-label">Interactions</span>
            </div>
            <div className="eval-stat">
              <span className="eval-stat-value">{formatTime(evaluation.summary?.total_time_seconds)}</span>
              <span className="eval-stat-label">Total Time</span>
            </div>
          </div>

          <p className="exam-complete-note">Your responses are being evaluated. Results will be shared by your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <div className="portal-card">
        <h1>Assessment Portal</h1>
        <p className="welcome">Welcome, {data?.candidate_name}</p>
        <p className="process-tag">Process: {data?.process}</p>

        {!examStarted ? (
          <div className="start-exam-section">
            <p className="exam-instructions">
              You will be handling a customer conversation. Click below to start your session.
            </p>
            <button className="start-exam-btn" onClick={handleStartExam}>
              Start Assessment ▶
            </button>
          </div>
        ) : (
          <div className="sessions-status">
            <p className="exam-progress-hint">Your assessment is in progress. Sessions will appear as new customers arrive.</p>
            <div className="sessions-grid">
              {data?.sessions?.map(session => {
                // Allow starting next session from hub if previous session is completed
                const prevSession = data?.sessions?.find(s => s.id === session.id - 1);
                const canStartFromHub = session.status === 'not_started' && prevSession &&
                  (prevSession.status === 'completed' || prevSession.status === 'completed_incomplete');
                const isClickable = session.status === 'in_progress' || canStartFromHub;

                return (
                <div
                  key={session.id}
                  className={`session-card ${session.status} ${isClickable ? 'session-card-clickable' : ''}`}
                  onClick={() => {
                    if (isClickable) {
                      window.open(`${process.env.PUBLIC_URL || ''}/examportal/session/${session.id}`, '_blank');
                    }
                  }}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      window.open(`${process.env.PUBLIC_URL || ''}/examportal/session/${session.id}`, '_blank');
                    }
                  }}
                >
                  <h3>Session {session.id}</h3>
                  <p className="session-type">
                    {session.type === 'scenario' ? '📋 Scenario' : '💬 Direct'}
                  </p>
                  <p className="session-status-label">
                    {session.status === 'not_started' && !canStartFromHub && '⏳ Waiting...'}
                    {session.status === 'not_started' && canStartFromHub && '🟢 Ready to start'}
                    {session.status === 'in_progress' && '🟡 In Progress'}
                    {session.status === 'completed' && '✅ Completed'}
                    {session.status === 'completed_incomplete' && '⚠️ Incomplete'}
                  </p>
                  {session.status === 'in_progress' && (
                    <p className="session-resume-hint">Click to resume ↗</p>
                  )}
                  {canStartFromHub && (
                    <p className="session-resume-hint">Click to start ↗</p>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExamPortal;
